import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FileSpreadsheet,
  Download,
  Plus,
  Trash2,
  Calendar,
  Search,
  AlertCircle,
  Check,
  ChevronLeft,
  ChevronRight,
  DollarSign,
  TrendingUp,
  FileText,
  Settings,
  Home
} from 'lucide-react';
import axios from 'axios';
import { format } from 'date-fns';
import { ja } from 'date-fns/locale';

interface JournalEntry {
  id: number;
  transaction_date: string;
  transaction_id?: number;
  debit_account_code: string;
  debit_account_name: string;
  debit_amount: number;
  debit_tax_amount: number;
  debit_tax_code: string;
  debit_tax_name: string;
  credit_account_code: string;
  credit_account_name: string;
  credit_amount: number;
  credit_tax_amount: number;
  credit_tax_code: string;
  credit_tax_name: string;
  description: string;
  memo?: string;
  is_auto_generated: boolean;
  payment_type?: string;
  order_type?: string;
}

interface AccountTitle {
  code: string;
  name: string;
  default_side: 'debit' | 'credit';
  category: string;
}

interface TaxCategory {
  code: string;
  name: string;
  rate: number;
}

const AccountingManagement: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'list' | 'export' | 'master'>('list');
  const [journalEntries, setJournalEntries] = useState<JournalEntry[]>([]);
  const [accountTitles, setAccountTitles] = useState<AccountTitle[]>([]);
  const [taxCategories, setTaxCategories] = useState<TaxCategory[]>([]);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [exportProgress, setExportProgress] = useState<string>('');

  // 新規仕訳入力用
  const [newEntry, setNewEntry] = useState({
    transaction_date: format(new Date(), 'yyyy-MM-dd'),
    debit_account_code: '',
    debit_amount: 0,
    debit_tax_amount: 0,
    debit_tax_code: '00',
    credit_account_code: '',
    credit_amount: 0,
    credit_tax_amount: 0,
    credit_tax_code: '00',
    description: '',
    memo: ''
  });

  // 仕訳一覧を取得
  const fetchJournalEntries = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get('/api/accounting-export/journal-entries', {
        params: {
          year: selectedYear,
          month: selectedMonth,
          page: currentPage,
          limit: 20
        }
      });

      // レスポンスの構造をチェック
      if (response.data) {
        setJournalEntries(response.data.entries || []);
        if (response.data.pagination) {
          setTotalPages(response.data.pagination.totalPages || 1);
          setTotalCount(response.data.pagination.total || 0);
        } else {
          // paginationがない場合のフォールバック
          setTotalPages(1);
          setTotalCount(response.data.entries?.length || 0);
        }
      } else {
        setJournalEntries([]);
        setTotalPages(1);
        setTotalCount(0);
      }
    } catch (error) {
      console.error('仕訳取得エラー:', error);
      setJournalEntries([]);
      setTotalPages(1);
      setTotalCount(0);
    } finally {
      setIsLoading(false);
    }
  };

  // マスタデータを取得
  const fetchMasterData = async () => {
    try {
      const [accountRes, taxRes] = await Promise.all([
        axios.get('/api/accounting-export/account-titles'),
        axios.get('/api/accounting-export/tax-categories')
      ]);

      // データが配列であることを確認
      const accountData = Array.isArray(accountRes.data) ? accountRes.data :
                          (accountRes.data?.data ? accountRes.data.data : []);
      const taxData = Array.isArray(taxRes.data) ? taxRes.data :
                      (taxRes.data?.data ? taxRes.data.data : []);

      setAccountTitles(accountData);
      setTaxCategories(taxData);
    } catch (error) {
      console.error('マスタデータ取得エラー:', error);
      setAccountTitles([]);
      setTaxCategories([]);
    }
  };

  useEffect(() => {
    fetchJournalEntries();
    fetchMasterData();
  }, [selectedYear, selectedMonth, currentPage]);

  // 仕訳作成
  const handleCreateEntry = async () => {
    try {
      await axios.post('/api/accounting-export/journal-entry', newEntry);
      setShowCreateModal(false);
      fetchJournalEntries();
      // フォームリセット
      setNewEntry({
        transaction_date: format(new Date(), 'yyyy-MM-dd'),
        debit_account_code: '',
        debit_amount: 0,
        debit_tax_amount: 0,
        debit_tax_code: '00',
        credit_account_code: '',
        credit_amount: 0,
        credit_tax_amount: 0,
        credit_tax_code: '00',
        description: '',
        memo: ''
      });
    } catch (error) {
      console.error('仕訳作成エラー:', error);
      alert('仕訳の作成に失敗しました');
    }
  };

  // 仕訳削除
  const handleDeleteEntry = async (id: number) => {
    if (!confirm('この仕訳を削除してもよろしいですか？')) return;

    try {
      await axios.delete(`/api/accounting-export/journal-entry/${id}`);
      fetchJournalEntries();
    } catch (error) {
      console.error('仕訳削除エラー:', error);
      alert('仕訳の削除に失敗しました');
    }
  };

  // CSVエクスポート
  const handleExport = async () => {
    setExportProgress('エクスポート処理を開始しています...');
    try {
      const response = await axios.get('/api/accounting-export/aoiro-export', {
        params: {
          year: selectedYear,
          month: selectedMonth
        },
        responseType: 'blob'
      });

      // ダウンロード処理
      const blob = new Blob([response.data], { type: 'text/csv;charset=Shift_JIS' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `siwake_${selectedYear}${String(selectedMonth).padStart(2, '0')}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      setExportProgress('✅ エクスポートが完了しました');
      setTimeout(() => setExportProgress(''), 3000);
    } catch (error) {
      console.error('エクスポートエラー:', error);
      setExportProgress('❌ エクスポートに失敗しました');
    }
  };

  const handleRecommendedSalesExport = async () => {
    setExportProgress('売上仕訳CSVを出力しています...');
    try {
      const response = await axios.get('/api/accounting-export/aoiro', {
        params: {
          year: selectedYear,
          month: selectedMonth,
          dailySummary: true
        },
        responseType: 'blob'
      });

      const blob = new Blob([response.data], { type: 'text/csv;charset=Shift_JIS' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `売上仕訳_${selectedYear}年${selectedMonth}月.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      setExportProgress('✅ 売上仕訳CSVをダウンロードしました');
      setTimeout(() => setExportProgress(''), 3000);
    } catch (error) {
      console.error('売上仕訳エクスポートエラー:', error);
      setExportProgress('❌ 売上仕訳CSVの出力に失敗しました');
    }
  };

  const handleRecommendedExpenseExport = async () => {
    setExportProgress('経費仕訳CSVを出力しています...');
    try {
      const response = await axios.get('/api/accounting-export/expenses-aoiro', {
        params: {
          year: selectedYear,
          month: selectedMonth
        },
        responseType: 'blob'
      });

      const blob = new Blob([response.data], { type: 'text/csv;charset=Shift_JIS' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `経費仕訳_${selectedYear}年${selectedMonth}月.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      setExportProgress('✅ 経費仕訳CSVをダウンロードしました');
      setTimeout(() => setExportProgress(''), 3000);
    } catch (error) {
      console.error('経費仕訳エクスポートエラー:', error);
      setExportProgress('❌ 経費仕訳CSVの出力に失敗しました');
    }
  };

  // 消費税額を自動計算
  const calculateTax = (amount: number, taxCode: string) => {
    const taxCategory = taxCategories.find(t => t.code === taxCode);
    if (!taxCategory || taxCategory.rate === 0) return 0;
    return Math.floor(amount * taxCategory.rate / (100 + taxCategory.rate));
  };

  return (
    <div className="bg-gray-50 min-h-screen">
      <div className="container mx-auto p-4">
        {/* ヘッダー */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
              <FileSpreadsheet className="h-7 w-7 text-blue-600" />
              会計管理（みんなの青色申告連携）
            </h1>
            <div className="flex items-center gap-2">
              <button
                onClick={() => navigate('/home')}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
              >
                <Home className="w-5 h-5" />
                ホーム
              </button>
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(Number(e.target.value))}
                className="px-4 py-2 border rounded-lg"
              >
                {[2024, 2025, 2026].map(year => (
                  <option key={year} value={year}>{year}年</option>
                ))}
              </select>
              <select
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(Number(e.target.value))}
                className="px-4 py-2 border rounded-lg"
              >
                {Array.from({ length: 12 }, (_, i) => i + 1).map(month => (
                  <option key={month} value={month}>{month}月</option>
                ))}
              </select>
            </div>
          </div>

          {/* タブ */}
          <div className="flex gap-2">
            <button
              onClick={() => setActiveTab('list')}
              className={`px-6 py-3 rounded-lg font-medium transition-colors ${
                activeTab === 'list'
                  ? 'bg-blue-500 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <FileText className="inline h-4 w-4 mr-2" />
              仕訳一覧
            </button>
            <button
              onClick={() => setActiveTab('export')}
              className={`px-6 py-3 rounded-lg font-medium transition-colors ${
                activeTab === 'export'
                  ? 'bg-blue-500 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <Download className="inline h-4 w-4 mr-2" />
              CSVエクスポート
            </button>
            <button
              onClick={() => setActiveTab('master')}
              className={`px-6 py-3 rounded-lg font-medium transition-colors ${
                activeTab === 'master'
                  ? 'bg-blue-500 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <Settings className="inline h-4 w-4 mr-2" />
              マスタ設定
            </button>
          </div>
        </div>

        {/* 仕訳一覧タブ */}
        {activeTab === 'list' && (
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-lg font-semibold">
                {selectedYear}年{selectedMonth}月の仕訳 （{totalCount}件）
              </h2>
              <button
                onClick={() => setShowCreateModal(true)}
                className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors flex items-center gap-2"
              >
                <Plus className="h-4 w-4" />
                仕訳を作成
              </button>
            </div>

            {/* 仕訳テーブル */}
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-y">
                  <tr>
                    <th className="px-4 py-3 text-left">日付</th>
                    <th className="px-4 py-3 text-left">借方科目</th>
                    <th className="px-4 py-3 text-right">借方金額</th>
                    <th className="px-4 py-3 text-right">借方税額</th>
                    <th className="px-4 py-3 text-left">貸方科目</th>
                    <th className="px-4 py-3 text-right">貸方金額</th>
                    <th className="px-4 py-3 text-right">貸方税額</th>
                    <th className="px-4 py-3 text-left">摘要</th>
                    <th className="px-4 py-3 text-center">操作</th>
                  </tr>
                </thead>
                <tbody>
                  {journalEntries && journalEntries.length > 0 ? journalEntries.map((entry) => (
                    <tr key={entry.id} className="border-b hover:bg-gray-50">
                      <td className="px-4 py-3">
                        {format(new Date(entry.transaction_date), 'MM/dd')}
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-gray-900">{entry.debit_account_name}</div>
                        <div className="text-xs text-gray-500">{entry.debit_tax_name}</div>
                      </td>
                      <td className="px-4 py-3 text-right font-medium">
                        ¥{entry.debit_amount.toLocaleString()}
                      </td>
                      <td className="px-4 py-3 text-right text-gray-600">
                        {entry.debit_tax_amount > 0 && `¥${entry.debit_tax_amount.toLocaleString()}`}
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-gray-900">{entry.credit_account_name}</div>
                        <div className="text-xs text-gray-500">{entry.credit_tax_name}</div>
                      </td>
                      <td className="px-4 py-3 text-right font-medium">
                        ¥{entry.credit_amount.toLocaleString()}
                      </td>
                      <td className="px-4 py-3 text-right text-gray-600">
                        {entry.credit_tax_amount > 0 && `¥${entry.credit_tax_amount.toLocaleString()}`}
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-gray-900">{entry.description}</div>
                        {entry.is_auto_generated && (
                          <span className="text-xs bg-blue-100 text-blue-600 px-2 py-1 rounded">自動</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-center">
                        {!entry.is_auto_generated && (
                          <button
                            onClick={() => handleDeleteEntry(entry.id)}
                            className="text-red-500 hover:text-red-700"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        )}
                      </td>
                    </tr>
                  )) : (
                    <tr>
                      <td colSpan={8} className="px-4 py-8 text-center text-gray-500">
                        仕訳データがありません
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* ページネーション */}
            {totalPages > 1 && (
              <div className="flex justify-center items-center gap-4 mt-6">
                <button
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-50"
                >
                  <ChevronLeft className="h-5 w-5" />
                </button>
                <span className="text-sm text-gray-600">
                  {currentPage} / {totalPages} ページ
                </span>
                <button
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-50"
                >
                  <ChevronRight className="h-5 w-5" />
                </button>
              </div>
            )}
          </div>
        )}

        {/* CSVエクスポートタブ */}
        {activeTab === 'export' && (
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="max-w-2xl mx-auto">
              <h2 className="text-lg font-semibold mb-6">CSVエクスポート</h2>

              <div className="bg-blue-100 border-2 border-blue-300 rounded-lg p-6 mb-6">
                <h3 className="text-lg font-semibold text-blue-900 mb-4">会計ソフト連携（推奨）</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <button
                    onClick={handleRecommendedSalesExport}
                    className="px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-2 font-semibold"
                  >
                    <Download className="h-5 w-5" />
                    青色申告：売上仕訳（推奨）
                  </button>
                  <button
                    onClick={handleRecommendedExpenseExport}
                    className="px-4 py-3 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors flex items-center justify-center gap-2 font-semibold"
                  >
                    <Download className="h-5 w-5" />
                    青色申告：経費仕訳（推奨）
                  </button>
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                <div className="flex items-start gap-3">
                  <AlertCircle className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-blue-800">
                    <p className="font-semibold mb-1">みんなの青色申告 対応形式</p>
                    <p>エクスポートされるCSVファイルは「みんなの青色申告」の標準インポート形式（全45列）に準拠しています。</p>
                    <p className="mt-2">文字コード: Shift_JIS / 改行コード: CRLF</p>
                  </div>
                </div>
              </div>

              <h3 className="text-base font-semibold text-gray-700 mb-3">旧/非推奨（従来導線）</h3>
              <div className="bg-gray-50 rounded-lg p-6 mb-6">
                <div className="text-center">
                  <Calendar className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-lg font-medium mb-2">
                    {selectedYear}年{selectedMonth}月の仕訳データ
                  </p>
                  <p className="text-sm text-gray-600 mb-6">
                    登録件数: {totalCount}件
                  </p>
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 text-left">
                    <p className="text-sm font-semibold text-red-800">旧CSVは使用禁止です</p>
                    <p className="text-sm text-red-700 mt-1">旧CSVはcancelled取引を含む可能性があるため、非推奨ではなく使用禁止です。</p>
                    <p className="text-sm text-red-700 mt-1">青色申告：売上仕訳（推奨）を使用してください。</p>
                  </div>
                  <button
                    type="button"
                    disabled
                    aria-disabled="true"
                    className="px-6 py-3 bg-gray-300 text-gray-500 rounded-lg cursor-not-allowed flex items-center gap-2 mx-auto"
                  >
                    <Download className="h-5 w-5" />
                    CSVダウンロード（旧CSV・使用禁止）
                  </button>
                </div>
              </div>

              {exportProgress && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                  <p className="text-green-800">{exportProgress}</p>
                </div>
              )}

              <div className="border-t pt-6 mt-6">
                <h3 className="font-semibold mb-3">エクスポート内容</h3>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li className="flex items-start gap-2">
                    <Check className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" />
                    <span>POSレジから自動生成された売上仕訳</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" />
                    <span>手動で作成した仕訳</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" />
                    <span>消費税額の自動計算（内税方式）</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" />
                    <span>軽減税率（8%）対応（テイクアウト）</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        )}

        {/* マスタ設定タブ */}
        {activeTab === 'master' && (
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* 勘定科目 */}
              <div>
                <h3 className="text-lg font-semibold mb-4">勘定科目マスタ</h3>
                <div className="bg-gray-50 rounded-lg p-4 max-h-96 overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="sticky top-0 bg-gray-50">
                      <tr className="border-b">
                        <th className="text-left py-2">コード</th>
                        <th className="text-left py-2">科目名</th>
                        <th className="text-left py-2">分類</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Array.isArray(accountTitles) && accountTitles.map((account) => (
                        <tr key={account.code} className="border-b">
                          <td className="py-2 font-mono">{account.code}</td>
                          <td className="py-2">{account.name}</td>
                          <td className="py-2 text-gray-600">{account.category}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* 税区分 */}
              <div>
                <h3 className="text-lg font-semibold mb-4">税区分マスタ</h3>
                <div className="bg-gray-50 rounded-lg p-4 max-h-96 overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="sticky top-0 bg-gray-50">
                      <tr className="border-b">
                        <th className="text-left py-2">コード</th>
                        <th className="text-left py-2">税区分</th>
                        <th className="text-right py-2">税率</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Array.isArray(taxCategories) && taxCategories.map((tax) => (
                        <tr key={tax.code} className="border-b">
                          <td className="py-2 font-mono">{tax.code}</td>
                          <td className="py-2">{tax.name}</td>
                          <td className="py-2 text-right">{tax.rate}%</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <p className="text-sm text-yellow-800">
                <strong>注意:</strong> マスタデータは「みんなの青色申告」の設定と一致させる必要があります。
                必要に応じて会計ソフト側の勘定科目コード・税区分コードを確認してください。
              </p>
            </div>
          </div>
        )}

        {/* 仕訳作成モーダル */}
        {showCreateModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
              <h3 className="text-lg font-semibold mb-4">新規仕訳作成</h3>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1">取引日付</label>
                  <input
                    type="date"
                    value={newEntry.transaction_date}
                    onChange={(e) => setNewEntry({...newEntry, transaction_date: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                </div>

                {/* 借方 */}
                <div className="border-l-4 border-blue-500 pl-4">
                  <h4 className="font-medium mb-2">借方</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">勘定科目</label>
                      <select
                        value={newEntry.debit_account_code}
                        onChange={(e) => setNewEntry({...newEntry, debit_account_code: e.target.value})}
                        className="w-full px-3 py-2 border rounded-lg"
                      >
                        <option value="">選択してください</option>
                        {accountTitles.map(account => (
                          <option key={account.code} value={account.code}>
                            {account.code} - {account.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">税区分</label>
                      <select
                        value={newEntry.debit_tax_code}
                        onChange={(e) => {
                          const taxCode = e.target.value;
                          const taxAmount = calculateTax(newEntry.debit_amount, taxCode);
                          setNewEntry({...newEntry, debit_tax_code: taxCode, debit_tax_amount: taxAmount});
                        }}
                        className="w-full px-3 py-2 border rounded-lg"
                      >
                        {taxCategories.map(tax => (
                          <option key={tax.code} value={tax.code}>
                            {tax.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">金額（税込）</label>
                      <input
                        type="number"
                        value={newEntry.debit_amount}
                        onChange={(e) => {
                          const amount = Number(e.target.value);
                          const taxAmount = calculateTax(amount, newEntry.debit_tax_code);
                          setNewEntry({...newEntry, debit_amount: amount, debit_tax_amount: taxAmount});
                        }}
                        className="w-full px-3 py-2 border rounded-lg"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">消費税額</label>
                      <input
                        type="number"
                        value={newEntry.debit_tax_amount}
                        onChange={(e) => setNewEntry({...newEntry, debit_tax_amount: Number(e.target.value)})}
                        className="w-full px-3 py-2 border rounded-lg bg-gray-50"
                      />
                    </div>
                  </div>
                </div>

                {/* 貸方 */}
                <div className="border-l-4 border-green-500 pl-4">
                  <h4 className="font-medium mb-2">貸方</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">勘定科目</label>
                      <select
                        value={newEntry.credit_account_code}
                        onChange={(e) => setNewEntry({...newEntry, credit_account_code: e.target.value})}
                        className="w-full px-3 py-2 border rounded-lg"
                      >
                        <option value="">選択してください</option>
                        {accountTitles.map(account => (
                          <option key={account.code} value={account.code}>
                            {account.code} - {account.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">税区分</label>
                      <select
                        value={newEntry.credit_tax_code}
                        onChange={(e) => {
                          const taxCode = e.target.value;
                          const taxAmount = calculateTax(newEntry.credit_amount, taxCode);
                          setNewEntry({...newEntry, credit_tax_code: taxCode, credit_tax_amount: taxAmount});
                        }}
                        className="w-full px-3 py-2 border rounded-lg"
                      >
                        {taxCategories.map(tax => (
                          <option key={tax.code} value={tax.code}>
                            {tax.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">金額（税込）</label>
                      <input
                        type="number"
                        value={newEntry.credit_amount}
                        onChange={(e) => {
                          const amount = Number(e.target.value);
                          const taxAmount = calculateTax(amount, newEntry.credit_tax_code);
                          setNewEntry({...newEntry, credit_amount: amount, credit_tax_amount: taxAmount});
                        }}
                        className="w-full px-3 py-2 border rounded-lg"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">消費税額</label>
                      <input
                        type="number"
                        value={newEntry.credit_tax_amount}
                        onChange={(e) => setNewEntry({...newEntry, credit_tax_amount: Number(e.target.value)})}
                        className="w-full px-3 py-2 border rounded-lg bg-gray-50"
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">摘要</label>
                  <input
                    type="text"
                    value={newEntry.description}
                    onChange={(e) => setNewEntry({...newEntry, description: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg"
                    placeholder="取引内容を入力"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">メモ</label>
                  <textarea
                    value={newEntry.memo}
                    onChange={(e) => setNewEntry({...newEntry, memo: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg"
                    rows={2}
                    placeholder="必要に応じてメモを入力"
                  />
                </div>
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800"
                >
                  キャンセル
                </button>
                <button
                  onClick={handleCreateEntry}
                  className="px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                >
                  作成
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AccountingManagement;
